import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FormattedDate, FormattedNumber } from 'react-intl';
import { Rating } from '@mui/material';

import { closeDialog } from 'app/store/fuse/dialogSlice';
import useCanOpenPage from 'app/providers/acl/useCanOpenPage';

import { formatDuration, parseDate, type Duration } from 'app/main/components/Utils';
import Icon from 'app/main/components/Icon';
import Text, { type TextProps } from 'app/main/components/Text';
import MultiButton, { type MultiButtonProps } from 'app/main/components/buttons/MultiButton';
import SquareChip, { type SquareChipProps } from 'app/main/components/SquareChip';
import { type ViewItemButtonProps } from '../ViewPage';

type renderSquareChipProps = Omit<SquareChipProps, 'className' | 'label'> & {
	value?: string;
};

type CellWithTooltipProps = Omit<TextProps, 'children' | 'tooltip'> & {
	value?: React.ReactNode;
};

const CellWithTooltip = ({ className, value, to, ...otherProps }: CellWithTooltipProps) => {
	const dispatch = useDispatch();
	const { canOpenDetailsPage } = useCanOpenPage();

	const url = to ? (typeof to !== 'string' ? canOpenDetailsPage(to?.module, to?.uid) : to) : undefined;

	return (
		<>
			<Text className={clsx(className, 'max-w-full text-ellipsis')} tooltip={value} {...otherProps}>
				{value}
			</Text>
			{!!value && !!url && (
				<Link to={url} style={{ color: 'inherit' }} onClick={() => dispatch(closeDialog())}>
					<Icon className="mx-4" icon="open-in-new" size={14} />
				</Link>
			)}
		</>
	);
};

export const renderMenu = ({ component, buttonProps, items }: MultiButtonProps<ViewItemButtonProps>) => {
	return <MultiButton component={component} buttonProps={buttonProps} items={items} size="small" />;
};

export const renderCellWithTooltip = (props: CellWithTooltipProps) => {
	if (props.value) return <CellWithTooltip {...props} />;
	return null;
};

export const renderSquareChip = (props: renderSquareChipProps) => {
	if (props.value)
		return (
			<SquareChip
				label={props.value}
				backgroundColor={props.backgroundColor}
				icon={props.icon}
				onClick={props.onClick}
				disabled={props.disabled}
			/>
		);

	return null;
};

export const renderDate = ({ value, format }: { value?: string; format?: Intl.DateTimeFormatOptions }) => {
	format = format || { day: '2-digit', month: '2-digit', year: 'numeric' };
	const parsedDate = parseDate(value);
	const date = parsedDate ? <FormattedDate value={parsedDate.toDate()} {...format} /> : undefined;
	return <Text tooltip={date}>{date}</Text>;
};

export const renderPercentage = ({ value, digits = 2 }: { value?: number; digits?: number }) => {
	if (value)
		return (
			<FormattedNumber
				value={value / 100}
				style="percent"
				maximumFractionDigits={digits}
				minimumFractionDigits={digits}
			/>
		);
	return '--';
};

export const renderCurrency = ({ value }: { value?: number }) => {
	if (value) return <FormattedNumber value={value} style="currency" currency="EUR" useGrouping="always" />;
	return '--';
};

export const renderRating = ({ value }: { value?: number }) => (
	<Rating
		value={((value || 0) * 5) / 100}
		max={5}
		icon={<Icon icon="fire" size={22} />}
		emptyIcon={<Icon icon="dot" size={22} />}
		readOnly
	/>
);

export const renderDuration = (params: Duration) => {
	const values = Object.values(params);
	return values.find(value => value > 0) ? formatDuration(params) : '--';
};

export default {
	renderCellWithTooltip,
	renderCurrency,
	renderDate,
	renderDuration,
	renderMenu,
	renderPercentage,
	renderRating,
	renderSquareChip
};
